/**
 * Data layer for `gamification-engine-points-levels-badges-goal-tracker-ui`. Thin typed fetch wrappers over /api/v1/gamification-engine-points-levels-badges-goal-tracker-ui.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface GamificationEnginePointsLevelsBadgesGoalTrackerUiRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListGamificationEnginePointsLevelsBadgesGoalTrackerUiResponse {
	count: number;
	items: GamificationEnginePointsLevelsBadgesGoalTrackerUiRow[];
}

export interface CreateGamificationEnginePointsLevelsBadgesGoalTrackerUiBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/gamification-engine-points-levels-badges-goal-tracker-ui";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListGamificationEnginePointsLevelsBadgesGoalTrackerUiResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListGamificationEnginePointsLevelsBadgesGoalTrackerUiResponse>(res);
}

export async function create(body: CreateGamificationEnginePointsLevelsBadgesGoalTrackerUiBody): Promise<GamificationEnginePointsLevelsBadgesGoalTrackerUiRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<GamificationEnginePointsLevelsBadgesGoalTrackerUiRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const gamificationEnginePointsLevelsBadgesGoalTrackerUiQueryKeys = {
	all: ["gamification-engine-points-levels-badges-goal-tracker-ui"] as const,
	list: () => [...gamificationEnginePointsLevelsBadgesGoalTrackerUiQueryKeys.all] as const,
	detail: (id: string) => [...gamificationEnginePointsLevelsBadgesGoalTrackerUiQueryKeys.all, id] as const,
};
